import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import "./Careers.css";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    resume: null,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [resumeError, setResumeError] = useState("");

  const [error, setError] = useState("");

  /* --------------------------------
     FETCH JOBS
  -------------------------------- */
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/careers`)
      .then((res) => setJobs(res.data))
      .catch(() => setError("Failed to load jobs"));
  }, []);

  /* --------------------------------
     FORM CHANGE
  -------------------------------- */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* --------------------------------
     APPLY CLICK
  -------------------------------- */
  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setSuccess("");
    setError("");
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  /* --------------------------------
   SUBMIT APPLICATION
-------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!selectedJob) {
      setError("Please select a job");
      return;
    }

    if (!form.name || !form.email || !form.phone) {
      setError("Please fill all required fields");
      return;
    }

    if (!form.resume) {
      setError("Please upload your resume (PDF, max 10MB)");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("experience", form.experience);
      formData.append("jobId", selectedJob._id);

      // 🔴 MOST IMPORTANT LINE
      formData.append("resume", form.resume);

      await axios.post(`${API_BASE_URL}/api/careers/apply`, formData);

      setSuccess(
        "✅ Application submitted successfully! Check your email for confirmation."
      );

      setForm({
        name: "",
        email: "",
        phone: "",
        experience: "",
        resume: null,
      });

      setTimeout(() => {
        setSelectedJob(null);
      }, 3000);
    } catch (err) {
      if (err.response?.status === 409) {
        setError("⚠️ You have already applied for this job with this email.");
      } else {
        setError("❌ Failed to submit application. Please try again.");
      }
    }
  };

  const scrollToJobs = () => {
    document.getElementById("openings").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="career-page">
      {/* ===== HERO SECTION ===== */}
      <section className="careers-hero">
        <div className="ch-left">
          <h1>
            Careers at <br />
            <span>Flytutor</span>
          </h1>
          <p className="ch-desc">
            Embark on a transformative journey with Flytutor's innovative drone training ecosystem. Elevate your career trajectory with industry leaders and captivate the audience through modern aviation.
          </p>
          <div className="ch-buttons">
            <button className="ch-btn-primary" onClick={scrollToJobs}>
              View Openings
            </button>
          </div>
          <div className="ch-footer">
            <p>
              We specialize in crafting exceptional aviation experiences for startups and enterprise clients.
            </p>
            <div className="ch-avatars">
              <div className="av">FT</div>
              <div className="av">JD</div>
              <div className="av">SA</div>
            </div>
          </div>
        </div>

        <div className="ch-right">
          <div className="ch-img ch-img-1"></div>
          <div className="ch-img ch-img-2"></div>
        </div>
      </section>

      {/* ===== CORE VALUES SECTION ===== */}
      <section className="careers-values-wrap">
        <div className="cv-card">
          <div className="cv-item">
            <div className="cv-icon">🚀</div>
            <h4>Innovation First</h4>
            <p>
              Design workflows that are intuitive, user-friendly, and visually striking. We'll lead you through smooth paths that convert effortlessly.
            </p>
            <span onClick={scrollToJobs} className="cv-link">Learn More</span>
          </div>
          <div className="cv-item">
            <div className="cv-icon">🌱</div>
            <h4>Rapid Growth</h4>
            <p>
              From story-rich concepts to emotionally expressive engineering, our talented team members are brilliant masters of innovation.
            </p>
            <span onClick={scrollToJobs} className="cv-link">Learn More</span>
          </div>
          <div className="cv-item">
            <div className="cv-icon">💡</div>
            <h4>Creative Impact</h4>
            <p>
              Your potential is the vibrant pulse of our realm, transcending domains to encapsulate identity and forge unforgettable connections.
            </p>
            <span onClick={scrollToJobs} className="cv-link">Learn More</span>
          </div>
          <div className="cv-item">
            <div className="cv-icon">🛡️</div>
            <h4>Excellence</h4>
            <p>
              At the very foundation of our digital castle, our expert developers meticulously craft secure, scalable, and future-proof platforms.
            </p>
            <span onClick={scrollToJobs} className="cv-link">Learn More</span>
          </div>
        </div>

        <div className="cv-bottom-msg">
          <h2 className="flytutor-main-heading" style={{ marginBottom: '20px' }}>Embrace Flytutor's Support for Your Career Journey Ahead.</h2>
          <p>
            Step into the welcoming embrace of Flytutor, where our team awaits to guide and support you on your exciting digital journey.
          </p>
          <button className="cv-explore" onClick={scrollToJobs}>Explore Openings</button>
        </div>
      </section>

      {/* ===== OPENINGS SECTION ===== */}
      <section id="openings" className="careers-openings">
        <div className="co-header">
          <h2 className="flytutor-main-heading">Current Openings</h2>
        </div>

        <div className="job-list">
          {jobs.map((job) => {
            const isClosed = job.status === "closed";
            return (
              <div key={job._id} className="job-card">
                <h3>{job.title}</h3>
                <p className="job-meta">
                  {job.department} • {job.location}
                </p>
                <p className="job-meta">
                  {job.type} • {job.mode}
                </p>
                <p>
                  <strong>Qualification:</strong>
                  <ul className="job-desc space-y-2 list-disc pl-5">
                  {Array.isArray(job.qualifications)
                    ? job.qualifications.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))
                    : job.qualifications
                      .split(". ")
                      .filter((p) => p.trim())
                      .map((point, index) => (
                        <li key={index}>{point.trim()}</li>
                      ))}
                </ul>
                </p>
                <p>
                  <strong>Experience:</strong> {job.experience || "Any"}
                </p>
                <p>
                  <strong>Vacancies:</strong> {job.vacancies || "Not specified"}
                </p>
                <p>
                  <strong>Key Responsibilities:</strong>
                  <ul className="job-desc space-y-2 list-disc pl-5">
                  {Array.isArray(job.description)
                    ? job.description.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))
                    : job.description
                      .split(". ")
                      .filter((p) => p.trim())
                      .map((point, index) => (
                        <li key={index}>{point.trim()}</li>
                      ))}
                </ul>
                </p>
                <button
                  className={`apply-btn ${isClosed ? "closed-btn" : ""}`}
                  disabled={isClosed}
                  onClick={() => handleApplyClick(job)}
                >
                  {isClosed ? "Closed" : "Apply"}
                </button>
              </div>
            );
          })}

          {jobs.length === 0 && (
            <div className="no-jobs-msg">
              <h3>No openings right now</h3>
              <p>Please check back later or drop your resume at careers@flytutor.in!</p>
            </div>
          )}
        </div>

        {/* ---------------- APPLY FORM ---------------- */}
        {selectedJob && (
          <form className="career-form" onSubmit={handleSubmit}>
            <h3 className="apply-heading">
              Applying for: <span>{selectedJob.title}</span>
            </h3>

            {error && <p className="error-text">{error}</p>}
            {success && <p className="success-text">{success}</p>}

            <input
              type="text"
              name="name"
              placeholder="Full Name *"
              value={form.name}
              onChange={handleChange}
            />

            <input
              type="email"
              name="email"
              placeholder="Email *"
              value={form.email}
              onChange={handleChange}
            />

            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              value={form.phone}
              onChange={handleChange}
            />

            <input
              type="text"
              name="experience"
              placeholder="Experience (e.g. 2 Years)"
              value={form.experience}
              onChange={handleChange}
            />
            <p className="file-hint">📄 PDF only • Max size 10 MB</p>

            {resumeError && <p className="error-text">{resumeError}</p>}

            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const file = e.target.files[0];
                setResumeError("");
                if (!file) return;
                if (file.type !== "application/pdf") {
                  setResumeError("❌ Only PDF files are allowed");
                  e.target.value = null;
                  return;
                }
                if (file.size > 10 * 1024 * 1024) {
                  setResumeError("❌ Resume must be under 10 MB");
                  e.target.value = null;
                  return;
                }
                setForm({ ...form, resume: file });
              }}
              required
            />

            <button type="submit" disabled={loading || !!resumeError}>
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
